import { Component, Inject } from '@angular/core';
import {
	FormControl,
	FormGroup,
	ReactiveFormsModule,
	Validators
} from '@angular/forms';
import {
	LO_DIALOG_DATA,
	LoButton,
	LoDialog,
	LoDialogRef,
	LoIcon,
	LoRadioGroup,
	LoRadioOption,
	LoTextarea
} from 'lo-design-system';
import {
	CANCELLATION_REASONS,
	CancelSubscriptionDialogData,
	CancelSubscriptionDialogResult,
	CancelSubscriptionFeedback
} from './cancel-subscription-dialog.model';

interface CancelFeedbackForm {
	stars_count: FormControl<number | null>;
	reason_to_cancel: FormControl<string | null>;
	will_return: FormControl<string | null>;
	suggestion: FormControl<string>;
}

@Component({
	standalone: true,
	selector: 'app-cancel-subscription-dialog',
	templateUrl: './cancel-subscription-dialog.component.html',
	styleUrls: ['./cancel-subscription-dialog.component.scss'],
	imports: [
		ReactiveFormsModule,
		LoButton,
		LoDialog,
		LoIcon,
		LoRadioGroup,
		LoTextarea
	]
})
export class CancelSubscriptionDialogComponent {
	readonly reasons = CANCELLATION_REASONS;
	readonly yesNo: string[] = ['Yes', 'No'];
	readonly ratingOptions: LoRadioOption[] = [1, 2, 3, 4, 5].map(star => ({
		value: String(star),
		label: String(star)
	}));
	readonly reasonOptions: LoRadioOption[] = CANCELLATION_REASONS.map(
		reason => ({ value: reason, label: reason })
	);
	readonly yesNoOptions: LoRadioOption[] = this.yesNo.map(choice => ({
		value: choice,
		label: choice
	}));
	readonly benefits: string[] = [
		'Access to exclusive content and features',
		'Regular updates and improvements',
		'Priority customer support',
		'Special discounts and offers'
	];

	// Plain field (not a getter): the rating group reads it on every
	// change-detection pass.
	rating = 0;

	readonly form = new FormGroup<CancelFeedbackForm>({
		stars_count: new FormControl<number | null>(null),
		reason_to_cancel: new FormControl<string | null>(
			null,
			Validators.required
		),
		will_return: new FormControl<string | null>(null, Validators.required),
		suggestion: new FormControl<string>('', { nonNullable: true })
	});

	constructor(
		public dialogRef: LoDialogRef<
			CancelSubscriptionDialogComponent,
			CancelSubscriptionDialogResult
		>,
		@Inject(LO_DIALOG_DATA) public data: CancelSubscriptionDialogData
	) {}

	setStars(count: number): void {
		this.rating = count;
		this.form.controls.stars_count.setValue(count);
	}

	confirm(): void {
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}
		const value = this.form.getRawValue();
		const suggestion = value.suggestion === '' ? null : value.suggestion;
		const feedback: CancelSubscriptionFeedback = {
			stars_count: value.stars_count,
			reason_to_cancel: value.reason_to_cancel,
			will_return: value.will_return as 'Yes' | 'No' | null,
			suggestion
		};
		this.dialogRef.close({ confirmed: true, feedback });
	}

	dismiss(): void {
		this.dialogRef.close({ confirmed: false });
	}
}
